import React, { useState, useEffect } from "react";
import "./ManagerPage.css";
import { useMaterialManager } from "../hooks/useMaterialManager";
import { useContentsList } from "../hooks/useContentsList";
import { useFetch } from "../hooks/useFetch";
import { API_CONFIG } from "../config/api";
import ContentSelectionModal from "../Components/ContentSelectionModal/ContentSelectionModal";
import { useNotifications } from "../hooks/useNotifications";
import { useSocials } from "../hooks/useSocials";
import { useTemplates } from "../hooks/useTemplates";
import ConteudosTab from "./Manager/components/ConteudosTab";
import NotificacoesTab from "./Manager/components/NotificacoesTab";
import SocialsTab from "./Manager/components/SocialsTab";
import MateriaisTab from "./Manager/components/MateriaisTab";
import AcessosTab from "./Manager/components/AcessosTab";
import TemplatesTab from "./Manager/components/TemplatesTab";

const ManagerPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("conteudos");
  const { fetchMaterials, fetchMaterialById, updateMaterial, materialState } =
    useMaterialManager();

  // Estados para criação de material
  const [selectedContentIds, setSelectedContentIds] = useState<string[]>([]);
  const [showContentSelectionModal, setShowContentSelectionModal] =
    useState(false);

  // Estado para preview de conteúdo
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<any>(null);

  // Estado para modal de gerenciamento de material
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<any>(null);

  // Estado para modal de confirmação de deletar material
  const [showDeleteMaterialModal, setShowDeleteMaterialModal] = useState(false);
  const [materialToDelete, setMaterialToDelete] = useState<string | null>(null);
  const [materialContents, setMaterialContents] = useState<any[]>([]);
  const [editingMaterial, setEditingMaterial] = useState(false);
  const [editMaterialForm, setEditMaterialForm] = useState({
    title: "",
    description: "",
    keywords: [] as string[],
    keywordsInput: "",
    referenceIdInput: "",
    introductionUrl: "",
    purchaseUrl: "",
    isFree: false,
    referenceIds: [] as string[],
    templateId: "",
    groups: [] as { name: string; contentIds: string[] }[],
  });

  // Estado para gerenciar grupos na edição
  const [newGroupName, setNewGroupName] = useState("");
  const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);

  // Estado para adicionar conteúdos a material existente
  const [isAddingToExistingMaterial, setIsAddingToExistingMaterial] =
    useState(false);

  // Hook para listar conteúdos
  const { contents, fetchContents } = useContentsList();

  // Hooks para notificações e socials
  const { fetchNotifications, notificationState } = useNotifications();
  const { fetchSocials, socialState } = useSocials();
  const { templates, fetchTemplates } = useTemplates();

  const tabs = [
    { id: "conteudos", label: "Conteúdos" },
    { id: "materiais", label: "Materiais" },
    { id: "templates", label: "Templates" },
    { id: "notificacoes", label: "Notificações" },
    { id: "socials", label: "Redes Sociais" },
    { id: "acessos", label: "Acessos" },
  ];

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === "conteudos") {
      if (contents.length === 0) {
        fetchContents();
      }
    } else if (activeTab === "materiais") {
      if (materialState.materials.length === 0) {
        fetchMaterials();
      }
      // Sempre carregar conteúdos na aba de materiais para exibir os selecionados
      fetchContents();
    } else if (activeTab === "notificacoes") {
      if (notificationState.notifications.length === 0) {
        fetchNotifications();
      }
    } else if (activeTab === "socials") {
      if (socialState.socials.length === 0) {
        fetchSocials();
      }
    }
  }, [
    activeTab,
    fetchMaterials,
    fetchContents,
    fetchNotifications,
    fetchSocials,
    contents.length,
    materialState.materials.length,
    notificationState.notifications.length,
    socialState.socials.length,
  ]);

  // Funções para materiais
  const handleContentSelection = async (contentIds: string[]) => {
    if (activeGroupIndex !== null) {
      // Adicionando conteúdo a um grupo específico na edição
      setEditMaterialForm(prev => {
        const newGroups = [...prev.groups];
        if (newGroups[activeGroupIndex]) {
          const group = newGroups[activeGroupIndex];
          // Add unique IDs
          const newIds = contentIds.filter(id => !group.contentIds.includes(id));
          group.contentIds = [...group.contentIds, ...newIds];
        }
        return { ...prev, groups: newGroups };
      });
      setActiveGroupIndex(null);
      setShowContentSelectionModal(false);
      return;
    }

    if (isAddingToExistingMaterial) {
      try {
        // Buscar material atual para obter contentIds existentes
        const currentMaterial = await fetchMaterialById(selectedMaterial.id);

        // Extrair contentIds existentes - pode vir como contents (array de objetos) ou contentIds (array de strings)
        let currentContentIds: string[] = [];
        if (
          currentMaterial.contents &&
          Array.isArray(currentMaterial.contents)
        ) {
          currentContentIds = currentMaterial.contents.map(
            (content: any) => content.id
          );
        } else if (
          currentMaterial.contentIds &&
          Array.isArray(currentMaterial.contentIds)
        ) {
          currentContentIds = currentMaterial.contentIds;
        }

        // Combinar com os novos contentIds selecionados (sem duplicatas)
        const allContentIds = Array.from(
          new Set([...currentContentIds, ...contentIds])
        );

        // Atualizar material com a lista completa de contentIds
        // A API vai deletar todos e recriar com a nova lista
        await updateMaterial(selectedMaterial.id, {
          contentIds: allContentIds,
        });

        // Recarregar material atualizado
        const updatedMaterial = await fetchMaterialById(selectedMaterial.id);

        // Atualizar materialContents com os contents retornados
        let updatedContents: any[] = [];
        if (
          updatedMaterial.contents &&
          Array.isArray(updatedMaterial.contents)
        ) {
          updatedContents = updatedMaterial.contents;
        } else if (
          updatedMaterial.contentIds &&
          Array.isArray(updatedMaterial.contentIds)
        ) {
          updatedContents = updatedMaterial.contentIds.map((id: string) => ({
            id,
          }));
        }

        setMaterialContents(updatedContents);

        setIsAddingToExistingMaterial(false);
        setSelectedContentIds([]);
        setShowContentSelectionModal(false);
        setShowMaterialModal(true);
      } catch (error) {
        alert("Erro ao adicionar conteúdos ao material");
      }
    } else {
      setSelectedContentIds(contentIds);
      // Garantir que os conteúdos estejam carregados para exibir na tabela
      if (contents.length === 0) {
        await fetchContents();
      }
    }
  };

  const handlePreviewContent = (content: any) => {
    setPreviewContent(content);
    setShowPreviewModal(true);
  };

  const handleManageMaterial = async (material: any) => {
    setSelectedMaterial(material);
    setEditingMaterial(false);

    try {
      const materialWithContents = await fetchMaterialById(material.id);

      // Extrair contents - pode vir como array de objetos ou como contentIds
      let contentsList: any[] = [];
      if (
        materialWithContents.contents &&
        Array.isArray(materialWithContents.contents)
      ) {
        // Se contents é array de objetos, usar diretamente
        contentsList = materialWithContents.contents;
      } else if (
        materialWithContents.contentIds &&
        Array.isArray(materialWithContents.contentIds)
      ) {
        // Se contentIds é array de strings, precisamos buscar os conteúdos completos
        // Por enquanto, criar objetos mínimos com apenas o id
        contentsList = materialWithContents.contentIds.map((id: string) => ({
          id,
        }));
      }

      setMaterialContents(contentsList);

      // Preencher formulário com dados do material
      // A API pode retornar template como objeto ou templateId diretamente
      const templateId =
        materialWithContents.templateId ||
        materialWithContents.template?.id ||
        "";

      // Processar grupos para o formato de edição (apenas IDs dos conteúdos)
      const groupsForEdit = (materialWithContents.groups || []).map((g: any) => ({
        name: g.name,
        contentIds: g.contents ? g.contents.map((c: any) => c.id) : (g.contentIds || [])
      }));

      setEditMaterialForm({
        title: materialWithContents.title || "",
        description: materialWithContents.description || "",
        keywords: materialWithContents.keywords || [],
        keywordsInput: "",
        referenceIdInput: "",
        introductionUrl: materialWithContents.introductionUrl || "",
        purchaseUrl: materialWithContents.purchaseUrl || "",
        isFree: materialWithContents.isFree || false,
        referenceIds: materialWithContents.referenceIds || (materialWithContents.referenceId ? [materialWithContents.referenceId] : []),
        templateId: templateId,
        groups: groupsForEdit,
      });
    } catch (error) {
      setMaterialContents([]);
    }

    // Buscar templates se ainda não foram carregados
    if (templates.length === 0) {
      fetchTemplates();
    }

    setShowMaterialModal(true);
  };

  const handleRemoveContentFromMaterial = async (contentId: string) => {
    if (
      window.confirm(
        "Tem certeza que deseja remover este conteúdo do material?"
      )
    ) {
      const updatedContents = materialContents.filter(
        (content) => content.id !== contentId
      );
      setMaterialContents(updatedContents);

      // Sempre salvar quando remover conteúdo (a API espera a lista completa)
      if (selectedMaterial) {
        try {
          // Enviar lista completa de contentIds (sem o removido)
          await updateMaterial(selectedMaterial.id, {
            contentIds: updatedContents.map((c) => c.id),
          });

          // Recarregar material atualizado
          const updatedMaterial = await fetchMaterialById(selectedMaterial.id);
          let refreshedContents: any[] = [];
          if (
            updatedMaterial.contents &&
            Array.isArray(updatedMaterial.contents)
          ) {
            refreshedContents = updatedMaterial.contents;
          } else if (
            updatedMaterial.contentIds &&
            Array.isArray(updatedMaterial.contentIds)
          ) {
            refreshedContents = updatedMaterial.contentIds.map(
              (id: string) => ({ id })
            );
          }
          setMaterialContents(refreshedContents);

          await fetchMaterials();
        } catch (error) {
          alert("Erro ao remover conteúdo. Tente novamente.");
          // Recarregar material para reverter estado em caso de erro
          try {
            const materialWithContents = await fetchMaterialById(
              selectedMaterial.id
            );
            let contentsList: any[] = [];
            if (
              materialWithContents.contents &&
              Array.isArray(materialWithContents.contents)
            ) {
              contentsList = materialWithContents.contents;
            } else if (
              materialWithContents.contentIds &&
              Array.isArray(materialWithContents.contentIds)
            ) {
              contentsList = materialWithContents.contentIds.map(
                (id: string) => ({ id })
              );
            }
            setMaterialContents(contentsList);
          } catch (reloadError) {
            // Se não conseguir recarregar, manter estado atual
            console.error("Erro ao recarregar material:", reloadError);
          }
        }
      }
    }
  };

  const handleAddContentToMaterial = () => {
    setIsAddingToExistingMaterial(true);
    setShowMaterialModal(false);
    setShowContentSelectionModal(true);
  };

  const handleAddGroup = () => {
    if (!newGroupName.trim()) return;
    setEditMaterialForm(prev => ({
      ...prev,
      groups: [...prev.groups, { name: newGroupName, contentIds: [] }]
    }));
    setNewGroupName("");
  };

  const handleRemoveGroup = (index: number) => {
    setEditMaterialForm(prev => {
      const newGroups = [...prev.groups];
      newGroups.splice(index, 1);
      return { ...prev, groups: newGroups };
    });
  };

  const handleAddContentToGroup = (index: number) => {
    setActiveGroupIndex(index);
    setShowContentSelectionModal(true);
  };

  const handleRemoveContentFromGroup = (groupIndex: number, contentId: string) => {
    setEditMaterialForm(prev => {
      const newGroups = [...prev.groups];
      const group = newGroups[groupIndex];
      group.contentIds = group.contentIds.filter(id => id !== contentId);
      return { ...prev, groups: newGroups };
    });
  };

  const handleMoveGroup = (index: number, direction: 'up' | 'down') => {
    setEditMaterialForm(prev => {
      const newGroups = [...prev.groups];
      if (direction === 'up' && index > 0) {
        [newGroups[index], newGroups[index - 1]] = [newGroups[index - 1], newGroups[index]];
      } else if (direction === 'down' && index < newGroups.length - 1) {
        [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];
      }
      return { ...prev, groups: newGroups };
    });
  };

  const handleMoveContentInGroup = (groupIndex: number, contentIndex: number, direction: 'up' | 'down') => {
    setEditMaterialForm(prev => {
      const newGroups = [...prev.groups];
      // Create a copy of the group to avoid mutating state directly
      const group = { ...newGroups[groupIndex] };
      const newContentIds = [...group.contentIds];
      
      if (direction === 'up' && contentIndex > 0) {
        [newContentIds[contentIndex], newContentIds[contentIndex - 1]] = [newContentIds[contentIndex - 1], newContentIds[contentIndex]];
      } else if (direction === 'down' && contentIndex < newContentIds.length - 1) {
        [newContentIds[contentIndex], newContentIds[contentIndex + 1]] = [newContentIds[contentIndex + 1], newContentIds[contentIndex]];
      }
      
      group.contentIds = newContentIds;
      newGroups[groupIndex] = group;
      return { ...prev, groups: newGroups };
    });
  };

  const handleSaveMaterial = async () => {
    if (!selectedMaterial) return;

    try {
      // Preparar templateId - enviar null se não houver template, ou o ID se houver
      const templateIdValue = editMaterialForm.templateId.trim() || null;

      const updateData: any = {
        title: editMaterialForm.title,
        description: editMaterialForm.description,
        keywords: editMaterialForm.keywords,
        introductionUrl: editMaterialForm.introductionUrl,
        purchaseUrl: editMaterialForm.purchaseUrl,
        isFree: editMaterialForm.isFree,
        referenceIds: editMaterialForm.referenceIds,
        contentIds: materialContents.map((c) => c.id),
        groups: editMaterialForm.groups,
      };

      // Sempre incluir templateId - null para remover, string para definir
      updateData.templateId = templateIdValue;

      await updateMaterial(selectedMaterial.id, updateData);

      // Atualizar material na lista
      await fetchMaterials();

      // Atualizar material selecionado e o formulário com os dados atualizados
      const updatedMaterial = await fetchMaterialById(selectedMaterial.id);

      // A API retorna template como objeto, não templateId diretamente
      // Extrair templateId do objeto template se existir
      const templateIdFromResponse =
        updatedMaterial.templateId || updatedMaterial.template?.id || "";

      setSelectedMaterial(updatedMaterial);

      // Atualizar materialContents com os contents retornados
      let updatedContents: any[] = [];
      if (updatedMaterial.contents && Array.isArray(updatedMaterial.contents)) {
        updatedContents = updatedMaterial.contents;
      } else if (
        updatedMaterial.contentIds &&
        Array.isArray(updatedMaterial.contentIds)
      ) {
        updatedContents = updatedMaterial.contentIds.map((id: string) => ({
          id,
        }));
      }
      setMaterialContents(updatedContents);

      // Processar grupos para o formato de edição
      const groupsForEdit = (updatedMaterial.groups || []).map((g: any) => ({
        name: g.name,
        contentIds: g.contents ? g.contents.map((c: any) => c.id) : (g.contentIds || [])
      }));

      // Atualizar o formulário com os dados retornados da API
      setEditMaterialForm({
        title: updatedMaterial.title || "",
        description: updatedMaterial.description || "",
        keywords: updatedMaterial.keywords || [],
        keywordsInput: "",
        referenceIdInput: "",
        introductionUrl: updatedMaterial.introductionUrl || "",
        purchaseUrl: updatedMaterial.purchaseUrl || "",
        isFree: updatedMaterial.isFree || false,
        referenceIds: updatedMaterial.referenceIds || (updatedMaterial.referenceId ? [updatedMaterial.referenceId] : []),
        templateId: templateIdFromResponse,
        groups: groupsForEdit,
      });

      setEditingMaterial(false);
      alert("Material atualizado com sucesso!");
    } catch (error) {
      alert("Erro ao salvar material. Tente novamente.");
    }
  };

  const handleCancelEditMaterial = () => {
    if (!selectedMaterial) return;

    // Restaurar valores originais
    // A API pode retornar template como objeto ou templateId diretamente
    const templateId =
      selectedMaterial.templateId || selectedMaterial.template?.id || "";

    // Processar grupos para o formato de edição
    const groupsForEdit = (selectedMaterial.groups || []).map((g: any) => ({
      name: g.name,
      contentIds: g.contents ? g.contents.map((c: any) => c.id) : (g.contentIds || [])
    }));

    setEditMaterialForm({
      title: selectedMaterial.title || "",
      description: selectedMaterial.description || "",
      keywords: selectedMaterial.keywords || [],
      referenceIdInput: "",
      keywordsInput: "",
      introductionUrl: selectedMaterial.introductionUrl || "",
      purchaseUrl: selectedMaterial.purchaseUrl || "",
      isFree: selectedMaterial.isFree || false,
      referenceIds: selectedMaterial.referenceIds || (selectedMaterial.referenceId ? [selectedMaterial.referenceId] : []),
      templateId: templateId,
      groups: groupsForEdit,
    });
    setEditingMaterial(false);
  };

  const { fetchWithAuth } = useFetch();

  const handleDeleteMaterial = (materialId: string) => {
    setMaterialToDelete(materialId);
    setShowDeleteMaterialModal(true);
  };

  const confirmDeleteMaterial = async () => {
    if (!materialToDelete) return;

    try {
      const response = await fetchWithAuth(
        `${API_CONFIG.BASE_URL}/materials/${materialToDelete}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        fetchMaterials();
        setShowDeleteMaterialModal(false);
        setMaterialToDelete(null);
        alert("Material deletado com sucesso!");
      } else {
        const errorText = await response.text();
        throw new Error(
          `Erro ao deletar material: ${response.status} - ${errorText}`
        );
      }
    } catch (error) {
      alert("Erro ao deletar material. Tente novamente.");
    }
  };

  const cancelDeleteMaterial = () => {
    setShowDeleteMaterialModal(false);
    setMaterialToDelete(null);
  };

  return (
    <div className="manager-container">
      <div className="manager-header">
        <h1 className="manager-title">Gerenciador</h1>
        <p className="manager-subtitle">
          Painel administrativo do Turminha Educa
        </p>
      </div>

      <div className="manager-tabs">
        <nav className="manager-tab-nav">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`manager-tab-item ${
                activeTab === tab.id ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="manager-content">
        <div className="manager-tab-content">
          {activeTab === "conteudos" && (
            <ConteudosTab onPreviewContent={handlePreviewContent} />
          )}
          {activeTab === "materiais" && (
            <MateriaisTab
              onShowContentSelectionModal={setShowContentSelectionModal}
              onPreviewContent={handlePreviewContent}
              selectedContentIds={selectedContentIds}
              setSelectedContentIds={setSelectedContentIds}
              contents={contents}
              materials={materialState.materials}
              onManageMaterial={handleManageMaterial}
              onDeleteMaterial={handleDeleteMaterial}
              onMaterialCreated={fetchMaterials}
            />
          )}
          {activeTab === "templates" && <TemplatesTab />}
          {activeTab === "notificacoes" && <NotificacoesTab />}
          {activeTab === "socials" && <SocialsTab />}
          {activeTab === "acessos" && <AcessosTab />}
        </div>
      </div>

      {/* Modal de Gerenciamento de Material */}
      {showMaterialModal && selectedMaterial && (
        <div
          className="modal-overlay"
          onClick={() => setShowMaterialModal(false)}
        >
          <div
            className="material-management-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="material-title-section">
                <h2 className="modal-title">
                  {editingMaterial ? "Editar Material" : selectedMaterial.title}
                </h2>
              </div>
              <button
                className="close-button"
                onClick={() => {
                  setShowMaterialModal(false);
                  setEditingMaterial(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {editingMaterial ? (
                <div className="material-edit-form">
                  <div className="form-group">
                    <label>Título:</label>
                    <input
                      type="text"
                      value={editMaterialForm.title}
                      onChange={(e) =>
                        setEditMaterialForm((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Título do material"
                    />
                  </div>

                  <div className="form-group">
                    <label>Descrição:</label>
                    <textarea
                      value={editMaterialForm.description}
                      onChange={(e) =>
                        setEditMaterialForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Descrição do material"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label>Palavras-chave:</label>
                    <div className="keywords-input-container">
                      {editMaterialForm.keywords.length > 0 && (
                        <div className="keywords-tags">
                          {editMaterialForm.keywords.map((keyword, index) => (
                            <span key={index} className="keyword-tag">
                              {keyword}
                              <button
                                type="button"
                                className="keyword-remove-btn"
                                onClick={() => {
                                  const newKeywords =
                                    editMaterialForm.keywords.filter(
                                      (k) => k !== keyword
                                    );
                                  setEditMaterialForm((prev) => ({
                                    ...prev,
                                    keywords: newKeywords,
                                  }));
                                }}
                                title="Remover palavra-chave"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        value={editMaterialForm.keywordsInput}
                        onChange={(e) => {
                          setEditMaterialForm((prev) => ({
                            ...prev,
                            keywordsInput: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            editMaterialForm.keywordsInput.trim()
                          ) {
                            e.preventDefault();
                            const newKeyword =
                              editMaterialForm.keywordsInput.trim();
                            if (
                              !editMaterialForm.keywords.some(
                                (k) =>
                                  k.toLowerCase() === newKeyword.toLowerCase()
                              )
                            ) {
                              const newKeywords = [
                                ...editMaterialForm.keywords,
                                newKeyword,
                              ];
                              setEditMaterialForm((prev) => ({
                                ...prev,
                                keywords: newKeywords,
                                keywordsInput: "",
                              }));
                            } else {
                              setEditMaterialForm((prev) => ({
                                ...prev,
                                keywordsInput: "",
                              }));
                            }
                          }
                        }}
                        placeholder="Digite uma palavra e pressione Enter"
                        className="keyword-input"
                      />
                    </div>
                    <small className="form-help">
                      💡 Digite uma palavra e pressione Enter para adicionar
                    </small>
                  </div>

                  <div className="form-group">
                    <label>URL de Introdução (Vídeo):</label>
                    <input
                      type="url"
                      value={editMaterialForm.introductionUrl}
                      onChange={(e) =>
                        setEditMaterialForm((prev) => ({
                          ...prev,
                          introductionUrl: e.target.value,
                        }))
                      }
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>

                  <div className="form-group">
                    <label>URL de Compra:</label>
                    <input
                      type="url"
                      value={editMaterialForm.purchaseUrl}
                      onChange={(e) =>
                        setEditMaterialForm((prev) => ({
                          ...prev,
                          purchaseUrl: e.target.value,
                        }))
                      }
                      placeholder="https://exemplo.com/comprar"
                    />
                  </div>

                  <div className="form-group">
                    <label>IDs de Referência:</label>
                    <div className="keywords-input-container">
                      {editMaterialForm.referenceIds.length > 0 && (
                        <div className="keywords-tags">
                          {editMaterialForm.referenceIds.map((refId, index) => (
                            <span key={index} className="keyword-tag">
                              {refId}
                              <button
                                type="button"
                                className="keyword-remove-btn"
                                onClick={() =>
                                  setEditMaterialForm((prev) => ({
                                    ...prev,
                                    referenceIds: prev.referenceIds.filter(
                                      (id) => id !== refId
                                    ),
                                  }))
                                }
                                title="Remover ID"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        value={editMaterialForm.referenceIdInput}
                        onChange={(e) =>
                          setEditMaterialForm((prev) => ({
                            ...prev,
                            referenceIdInput: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            editMaterialForm.referenceIdInput.trim()
                          ) {
                            e.preventDefault();
                            const newRefId = editMaterialForm.referenceIdInput.trim();
                            if (
                              !editMaterialForm.referenceIds.includes(newRefId)
                            ) {
                              setEditMaterialForm((prev) => ({
                                ...prev,
                                referenceIds: [...prev.referenceIds, newRefId],
                                referenceIdInput: "",
                              }));
                            } else {
                              setEditMaterialForm((prev) => ({
                                ...prev,
                                referenceIdInput: "",
                              }));
                            }
                          }
                        }}
                        placeholder="Digite um ID e pressione Enter"
                        className="keyword-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Template:</label>
                    <select
                      value={editMaterialForm.templateId}
                      onChange={(e) =>
                        setEditMaterialForm((prev) => ({
                          ...prev,
                          templateId: e.target.value,
                        }))
                      }
                    >
                      <option value="">Nenhum template</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editMaterialForm.isFree}
                        onChange={(e) =>
                          setEditMaterialForm((prev) => ({
                            ...prev,
                            isFree: e.target.checked,
                          }))
                        }
                      />
                      Material Gratuito
                    </label>
                  </div>

                  <div className="form-group" style={{ marginTop: '20px', borderTop: '1px solid #444', paddingTop: '20px' }}>
                    <label style={{ fontSize: '18px', marginBottom: '10px', display: 'block' }}>Grupos de Conteúdo</label>
                    
                    <div className="add-group-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                      <input
                        type="text"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        placeholder="Nome do novo grupo"
                        style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #444', backgroundColor: '#333', color: 'white' }}
                      />
                      <button
                        onClick={handleAddGroup}
                        disabled={!newGroupName.trim()}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: newGroupName.trim() ? 'pointer' : 'not-allowed',
                          opacity: newGroupName.trim() ? 1 : 0.6
                        }}
                      >
                        + Adicionar Grupo
                      </button>
                    </div>

                    {editMaterialForm.groups.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999', border: '1px dashed #444', borderRadius: '8px' }}>
                        Nenhum grupo criado. Adicione um grupo para começar a inserir conteúdos.
                      </div>
                    ) : (
                      <div className="groups-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {editMaterialForm.groups.map((group, groupIndex) => (
                          <div key={groupIndex} className="group-card" style={{ border: '1px solid #444', borderRadius: '8px', padding: '16px', backgroundColor: '#2B2B2B' }}>
                            <div className="group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #444', paddingBottom: '8px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleMoveGroup(groupIndex, 'up');
                                    }}
                                    disabled={groupIndex === 0}
                                    style={{ 
                                      cursor: groupIndex === 0 ? 'default' : 'pointer', 
                                      opacity: groupIndex === 0 ? 0.3 : 1, 
                                      border: 'none', 
                                      background: 'none', 
                                      color: 'white',
                                      fontSize: '10px',
                                      padding: 0
                                    }}
                                  >
                                    ▲
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleMoveGroup(groupIndex, 'down');
                                    }}
                                    disabled={groupIndex === editMaterialForm.groups.length - 1}
                                    style={{ 
                                      cursor: groupIndex === editMaterialForm.groups.length - 1 ? 'default' : 'pointer', 
                                      opacity: groupIndex === editMaterialForm.groups.length - 1 ? 0.3 : 1, 
                                      border: 'none', 
                                      background: 'none', 
                                      color: 'white',
                                      fontSize: '10px',
                                      padding: 0
                                    }}
                                  >
                                    ▼
                                  </button>
                                </div>
                                <h5 style={{ margin: 0, fontSize: '16px' }}>{group.name}</h5>
                              </div>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                  onClick={() => handleAddContentToGroup(groupIndex)}
                                  style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#2196f3',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  + Conteúdo
                                </button>
                                <button
                                  onClick={() => handleRemoveGroup(groupIndex)}
                                  style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  Remover
                                </button>
                              </div>
                            </div>

                            {group.contentIds.length === 0 ? (
                              <p style={{ color: '#999', fontStyle: 'italic', margin: 0, fontSize: '14px' }}>Nenhum conteúdo neste grupo.</p>
                            ) : (
                              <div className="selected-contents-table-container">
                                <table className="selected-contents-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #444', textAlign: 'left' }}>
                                      <th style={{ padding: '8px', width: '40px' }}>#</th>
                                      <th style={{ padding: '8px' }}>Nome</th>
                                      <th style={{ padding: '8px' }}>Tipo</th>
                                      <th style={{ padding: '8px', textAlign: 'right' }}>Ações</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.contentIds.map((contentId, contentIndex) => {
                                      const content = contents.find(c => c.id === contentId);
                                      if (!content) return null;
                                      
                                      return (
                                        <tr key={`${content.id}-${contentIndex}`} className="selected-content-row" style={{ borderBottom: '1px solid #333' }}>
                                          <td style={{ padding: '8px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                              <button 
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleMoveContentInGroup(groupIndex, contentIndex, 'up');
                                                }}
                                                disabled={contentIndex === 0}
                                                style={{ 
                                                  cursor: contentIndex === 0 ? 'default' : 'pointer', 
                                                  opacity: contentIndex === 0 ? 0.3 : 1, 
                                                  border: 'none', 
                                                  background: 'none', 
                                                  color: 'white',
                                                  fontSize: '10px',
                                                  padding: 0
                                                }}
                                              >
                                                ▲
                                              </button>
                                              <button 
                                                type="button"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleMoveContentInGroup(groupIndex, contentIndex, 'down');
                                                }}
                                                disabled={contentIndex === group.contentIds.length - 1}
                                                style={{ 
                                                  cursor: contentIndex === group.contentIds.length - 1 ? 'default' : 'pointer', 
                                                  opacity: contentIndex === group.contentIds.length - 1 ? 0.3 : 1, 
                                                  border: 'none', 
                                                  background: 'none', 
                                                  color: 'white',
                                                  fontSize: '10px',
                                                  padding: 0
                                                }}
                                              >
                                                ▼
                                              </button>
                                            </div>
                                          </td>
                                          <td style={{ padding: '8px' }}>{content.name}</td>
                                          <td style={{ padding: '8px' }}>
                                            <span className="content-type-badge" style={{ padding: '2px 6px', borderRadius: '4px', backgroundColor: '#444', fontSize: '12px' }}>
                                              {content.type}
                                            </span>
                                          </td>
                                          <td style={{ padding: '8px', textAlign: 'right' }}>
                                            <button
                                              onClick={() => handleRemoveContentFromGroup(groupIndex, content.id)}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '16px'
                                              }}
                                              title="Remover do grupo"
                                            >
                                              ❌
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-actions" style={{ marginTop: "20px" }}>
                    <button
                      className="save-material-btn"
                      onClick={handleSaveMaterial}
                      style={{
                        backgroundColor: "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "12px 24px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    >
                      💾 Salvar
                    </button>
                    <button
                      className="cancel-edit-btn"
                      onClick={handleCancelEditMaterial}
                      style={{
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        padding: "12px 24px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      ❌ Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="material-info-section">
                  <div className="material-details">
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Descrição:</strong> 
                      <div dangerouslySetInnerHTML={{ __html: selectedMaterial.description }} style={{ marginTop: '0.5rem' }} />
                    </div>
                    <p>
                      <strong>Total de Conteúdos:</strong>{" "}
                      {materialContents.length}
                    </p>
                    {selectedMaterial.keywords &&
                      selectedMaterial.keywords.length > 0 && (
                        <p>
                          <strong>Palavras-chave:</strong>{" "}
                          {selectedMaterial.keywords.join(", ")}
                        </p>
                      )}
                    {selectedMaterial.introductionUrl && (
                      <p>
                        <strong>URL de Introdução:</strong>{" "}
                        <a
                          href={selectedMaterial.introductionUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--white)",
                            textDecoration: "underline",
                          }}
                        >
                          {selectedMaterial.introductionUrl}
                        </a>
                      </p>
                    )}
                    {selectedMaterial.purchaseUrl && (
                      <p>
                        <strong>URL de Compra:</strong>{" "}
                        <a
                          href={selectedMaterial.purchaseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "var(--white)",
                            textDecoration: "underline",
                          }}
                        >
                          {selectedMaterial.purchaseUrl}
                        </a>
                      </p>
                    )}
                    <p>
                      <strong>IDs de Referência:</strong>{" "}
                      {selectedMaterial.referenceIds &&
                      selectedMaterial.referenceIds.length > 0
                        ? selectedMaterial.referenceIds.join(", ")
                        : selectedMaterial.referenceId || "N/A"}
                    </p>
                    <p>
                      <strong>Material Gratuito:</strong>{" "}
                      {selectedMaterial.isFree ? "Sim" : "Não"}
                    </p>
                  </div>

                  <div className="material-actions">
                    <button
                      className="edit-material-btn"
                      onClick={() => setEditingMaterial(true)}
                      style={{
                        backgroundColor: "#2196F3",
                        color: "white",
                        border: "none",
                        padding: "12px 24px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    >
                      ✏️ Editar Material
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Preview de Conteúdo */}
      {showPreviewModal && previewContent && (
        <div
          className="modal-overlay preview-overlay"
          onClick={() => setShowPreviewModal(false)}
        >
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Preview: {previewContent.name}</h2>
              <button
                className="close-button"
                onClick={() => setShowPreviewModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="preview-content-info">
                <div className="content-details">
                  <p>
                    <strong>Tipo:</strong> {previewContent.type}
                  </p>
                  <p>
                    <strong>Criado em:</strong>{" "}
                    {new Date(previewContent.createdAt).toLocaleDateString(
                      "pt-BR"
                    )}
                  </p>
                  {previewContent.description && (
                    <p>
                      <strong>Descrição:</strong> {previewContent.description}
                    </p>
                  )}
                </div>

                <div className="preview-content-area">
                  {previewContent.type === "png" ||
                  previewContent.type === "jpg" ||
                  previewContent.type === "jpeg" ? (
                    <div className="image-preview">
                      <img
                        src={previewContent.url}
                        alt={previewContent.name}
                        className="preview-image"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const nextElement = e.currentTarget
                            .nextElementSibling as HTMLElement;
                          if (nextElement) {
                            nextElement.style.display = "flex";
                          }
                        }}
                      />
                      <div
                        className="preview-error"
                        style={{ display: "none" }}
                      >
                        <p>❌ Não foi possível carregar a imagem</p>
                        <p>URL: {previewContent.url}</p>
                      </div>
                    </div>
                  ) : previewContent.type === "pdf" ? (
                    <div className="pdf-preview">
                      <iframe
                        src={previewContent.url}
                        className="preview-pdf"
                        title={previewContent.name}
                      />
                    </div>
                  ) : previewContent.type === "mp4" ? (
                    <div className="video-preview">
                      <video
                        controls
                        className="preview-video"
                        src={previewContent.url}
                      >
                        Seu navegador não suporta o elemento de vídeo.
                      </video>
                    </div>
                  ) : previewContent.type === "mp3" ? (
                    <div className="audio-preview">
                      <audio
                        controls
                        className="preview-audio"
                        src={previewContent.url}
                      >
                        Seu navegador não suporta o elemento de áudio.
                      </audio>
                    </div>
                  ) : previewContent.type === "txt" ? (
                    <div className="text-preview">
                      <iframe
                        src={previewContent.url}
                        className="preview-text"
                        title={previewContent.name}
                      />
                    </div>
                  ) : (
                    <div className="file-preview">
                      <div className="preview-placeholder">
                        <p>📄</p>
                        <p>Preview não disponível para este tipo de arquivo</p>
                        <p>Tipo: {previewContent.type}</p>
                        <a
                          href={previewContent.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-link"
                        >
                          📥 Baixar arquivo
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Conteúdos */}
      <ContentSelectionModal
        isOpen={showContentSelectionModal}
        onClose={() => {
          setShowContentSelectionModal(false);
          setIsAddingToExistingMaterial(false);
        }}
        onConfirm={handleContentSelection}
        onPreview={handlePreviewContent}
        title={
          isAddingToExistingMaterial
            ? `Adicionar Conteúdos ao Material: ${selectedMaterial?.title}`
            : "Selecionar Conteúdos para o Material"
        }
        existingContentIds={
          isAddingToExistingMaterial
            ? materialContents.map((c) => c.id) // ContentIds do material atual
            : selectedContentIds // ContentIds selecionados para novo material
        }
        materials={materialState.materials}
        isAddingToExisting={isAddingToExistingMaterial}
        currentMaterialId={selectedMaterial?.id}
      />

      {/* Modal de Confirmação para Deletar Material */}
      {showDeleteMaterialModal && (
        <div
          className="modal-overlay preview-overlay"
          onClick={cancelDeleteMaterial}
        >
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">⚠️ Confirmar Exclusão</h2>
              <button className="close-button" onClick={cancelDeleteMaterial}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-confirmation-content">
                <p className="delete-warning">
                  Tem certeza que deseja deletar este material?
                </p>
                <p className="delete-description">
                  Esta ação não pode ser desfeita. Todos os conteúdos associados
                  ao material serão desvinculados, mas não deletados.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={cancelDeleteMaterial}>
                Cancelar
              </button>
              <button
                className="delete-confirm-button"
                onClick={confirmDeleteMaterial}
              >
                🗑️ Deletar Material
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerPage;
